import AnalyticsStudio from "@/components/admin/AnalyticsStudio";
export { default as AnalyticsPanel } from "@/components/admin/AnalyticsStudio";
import { AdminSearch, AdminStatus, matchesAdminSearch } from "@/components/admin/AdminListTools";
import AdminReferrals from "@/components/admin/AdminReferrals";
import { useEffect, useState } from "react";
import api, { formatDkk } from "@/lib/api";
import { PageSkeleton } from "@/components/LoadingSkeleton";
import { expertBackgroundLabel } from "@/lib/expertBackground";
import AdminExpertReviewDialog from "@/components/admin/AdminExpertReviewDialog";
import AdminSeoLandings from "@/components/admin/AdminSeoLandings";
import AdminWorkspace, { AdminOverview, initialAdminTab } from "@/components/admin/AdminWorkspace";
import "./AdminDashboard.css";

const REQUEST_FILTERS = [
  ["all", "Alle"],
  ["waiting", "Afventer"],
  ["selected", "Ekspert valgt"],
  ["completed", "Færdige"],
  ["cancelled", "Annullerede"],
];

const requestStatusLabel = (status) => ({
  open: "Afventer eksperter",
  clarification: "Afventer oplysninger",
  offer_received: "Prisforslag modtaget",
  offer_accepted: "Ekspert valgt",
  completed: "Færdig",
  cancelled: "Annulleret",
  reserved: "Betaling behandles",
}[status] || "Ukendt status");

const requestMatchesView = (status, view) => {
  if (view === "all") return true;
  if (view === "waiting") return ["open", "clarification", "offer_received", "reserved"].includes(status);
  if (view === "selected") return status === "offer_accepted";
  return status === view;
};

const readableDate = (value) => value ? new Date(value).toLocaleString("da-DK") : "Ikke angivet";

export default function AdminDashboard() {
  const [tab, setTab] = useState(initialAdminTab);
  const [metrics, setMetrics] = useState({});
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userRole, setUserRole] = useState("all");
  const [expertSearch, setExpertSearch] = useState("");
  const [supportSearch, setSupportSearch] = useState("");
  const [qualificationSearch, setQualificationSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [supportError, setSupportError] = useState("");
  const [qualificationError, setQualificationError] = useState("");
  const [commissionMessage, setCommissionMessage] = useState("");
  const [commissionBusy, setCommissionBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [experts, setExperts] = useState([]);
  const [expertView, setExpertView] = useState("pending");
  const [expertCounts, setExpertCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0, draft: 0, demo: 0 });
  const [reviewExpertId, setReviewExpertId] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [problems, setProblems] = useState([]);
  const [problemView, setProblemView] = useState("all");
  const [problemSearch, setProblemSearch] = useState("");
  const [expandedProblemId, setExpandedProblemId] = useState(null);
  const [cats, setCats] = useState([]);
  const [commission, setCommission] = useState(0.20);
  const [newCat, setNewCat] = useState({ name: "", slug: "" });
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCat, setEditingCat] = useState({ name: "", slug: "" });
  const [deletingCatId, setDeletingCatId] = useState(null);
  const [replacementCatId, setReplacementCatId] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState("30d");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");
  const [qualificationClaims, setQualificationClaims] = useState([]);
  const [qualificationView, setQualificationView] = useState("pending");
  const [qualificationLoading, setQualificationLoading] = useState(false);
  const [supportCases, setSupportCases] = useState([]);
  const [supportView, setSupportView] = useState("open");
  const [supportLoading, setSupportLoading] = useState(false);

  const loadExperts = (view = expertView) =>
    api.get("/admin/experts", { params: { status: view } }).then((r) => setExperts(r.data));

  const loadExpertCounts = () =>
    api.get("/admin/experts/counts").then((response) => setExpertCounts(response.data));

  const reload = async (view = expertView) => {
    setLoadError("");
    try {
      const [metricResult, userResult, expertResult, countResult, problemResult, categoryResult, commissionResult] = await Promise.allSettled([
        api.get("/admin/metrics"), api.get("/admin/users"), api.get("/admin/experts", { params: { status: view } }),
        api.get("/admin/experts/counts"), api.get("/admin/problems"), api.get("/categories"), api.get("/admin/settings/commission"),
      ]);
      if ([metricResult, userResult, expertResult, countResult, problemResult, categoryResult, commissionResult].some((result) => result.status === "rejected")) setLoadError("Nogle oplysninger kunne ikke hentes. Tallene kan være ufuldstændige. Prøv at indlæse igen.");
      if (metricResult.status === "fulfilled") setMetrics(metricResult.value.data);
      if (userResult.status === "fulfilled") setUsers(userResult.value.data || []);
      if (expertResult.status === "fulfilled") setExperts(expertResult.value.data || []);
      if (countResult.status === "fulfilled") setExpertCounts(countResult.value.data);
      if (problemResult.status === "fulfilled") setProblems(problemResult.value.data || []);
      if (categoryResult.status === "fulfilled") setCats(categoryResult.value.data || []);
      if (commissionResult.status === "fulfilled") setCommission(commissionResult.value.data.commission);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload("pending"); }, []);

  useEffect(() => {
    if (tab !== "analytics") return;
    let active = true;
    setAnalyticsLoading(true);
    setAnalytics(null);
    setAnalyticsError("");
    api.get("/admin/analytics", { params: { date_range: analyticsRange } })
      .then((response) => { if (active) setAnalytics(response.data); })
      .catch(() => { if (active) setAnalyticsError("Statistikken kunne ikke indlæses. Prøv igen om lidt."); })
      .finally(() => { if (active) setAnalyticsLoading(false); });
    return () => { active = false; };
  }, [tab, analyticsRange]);

  const loadQualifications = (status = qualificationView) => {
    setQualificationLoading(true);
    setQualificationError("");
    return api.get("/admin/professional-badges", { params: { status } })
      .then((response) => setQualificationClaims(response.data || []))
      .finally(() => setQualificationLoading(false));
  };

  useEffect(() => {
    if (tab === "qualifications") loadQualifications(qualificationView).catch(() => setQualificationError("Dokumenterne kunne ikke hentes. Prøv igen."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, qualificationView]);

  const loadSupportCases = (status = supportView) => {
    setSupportLoading(true);
    setSupportError("");
    return api.get("/admin/support-cases", { params: { status } })
      .then((response) => setSupportCases(response.data || []))
      .finally(() => setSupportLoading(false));
  };

  useEffect(() => {
    if (tab === "support") loadSupportCases(supportView).catch(() => setSupportError("Supportsagerne kunne ikke hentes. Prøv igen."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, supportView]);

  if (loading) return <PageSkeleton cards={4}/>;

  const visibleExperts = experts.filter(e => matchesAdminSearch(expertSearch, e.name, e.email, e.headline, e.specialties || []));
  const visibleSupport = supportCases.filter(c => matchesAdminSearch(supportSearch, c.problem_title, c.description, c.reported_by_name, c.reported_by_email));
  const visibleQualifications = qualificationClaims.filter(c => matchesAdminSearch(qualificationSearch, c.title, c.label, c.expert_name, c.expert_email, c.issuer));
  const visibleCategories = cats.filter(c => matchesAdminSearch(categorySearch, c.name, c.slug));
  const userSearchValue = userSearch.trim().toLowerCase();
  const visibleUsers = users.filter((user) => (userRole === "all" || user.role === userRole) && [user.name, user.email].some((value) => String(value || "").toLowerCase().includes(userSearchValue)));
  const navigateAdmin = (nextTab) => {
    if (nextTab === "experts" && tab === "metrics") { setExpertView("pending"); loadExperts("pending").catch(() => {}); }
    if (nextTab === "problems" && tab === "metrics") { setProblemView("waiting"); setProblemSearch(""); }
    if (nextTab === "support" && tab === "metrics") setSupportView("open");
    setTab(nextTab);
  };
  const searchValue = problemSearch.trim().toLowerCase();
  const visibleProblems = problems.filter((problem) => {
    if (!requestMatchesView(problem.status, problemView)) return false;
    if (!searchValue) return true;
    return [
      problem.title, problem.description, problem.customer_name, problem.customer_email,
      problem.accepted_expert_name, problem.accepted_expert_email, problem.category_name, problem.id,
    ].some((value) => String(value || "").toLowerCase().includes(searchValue));
  });

  const decideExpert = async (id, decision, adminNote = null) => {
    await api.post(`/admin/experts/${id}/approve`, { decision, admin_note: adminNote });
    await Promise.all([loadExperts(), loadExpertCounts()]);
  };
  const removeAccount = async ({ id, name, email }) => {
    setRemovingUserId(id);
    try {
      const { data: impact } = await api.get(`/admin/users/${id}/removal-impact`);
      const historyCount = Object.values(impact.history || {}).reduce((sum, count) => sum + count, 0);
      const message = impact.action === "hard_delete"
        ? `Slet kontoen permanent?\n\n${name || "Ukendt bruger"}\n${email}\n\n${impact.is_demo_or_test ? "Dette er en demo-/testkonto." : "Kontoen har ingen markedspladshistorik."}\nFinansielle og øvrige auditdata slettes ikke.`
        : `Denne konto har ${historyCount} historikposter og kan derfor ikke slettes sikkert.\n\n${name || "Ukendt bruger"}\n${email}\n\nFortsæt med at deaktivere kontoen? Historik og finansielle data bevares.`;
      if (!window.confirm(message)) return;
      const { data } = await api.delete(`/admin/users/${id}`, { params: { confirm_email: email } });
      alert(data.action === "deactivated" ? "Kontoen er deaktiveret. Historikken er bevaret." : "Kontoen er slettet. Audit- og finansdata er bevaret.");
      await reload(expertView);
    } catch (error) {
      alert(error.response?.data?.detail || "Kontoen kunne ikke fjernes");
    } finally {
      setRemovingUserId(null);
    }
  };
  const addCat = async () => {
    if (!newCat.name || !newCat.slug) return;
    await api.post("/categories", { ...newCat, icon: "wrench" });
    setNewCat({ name: "", slug: "" });
    reload();
  };
  const saveCat = async (id) => {
    if (!editingCat.name.trim() || !editingCat.slug.trim()) return;
    await api.put(`/categories/${id}`, { name: editingCat.name.trim(), slug: editingCat.slug.trim() });
    setEditingCatId(null);
    reload();
  };
  const delCat = async (id) => {
    setCategoryBusy(true);
    setCategoryError("");
    try {
      await api.delete(`/categories/${id}`);
      setDeletingCatId(null);
      await reload();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setCategoryError(typeof detail === "object" ? detail.message : (detail || "Kategorien kunne ikke slettes"));
    } finally {
      setCategoryBusy(false);
    }
  };
  const mergeCat = async (id) => {
    if (!replacementCatId) return;
    setCategoryBusy(true);
    setCategoryError("");
    try {
      await api.post(`/categories/${id}/merge`, { target_category_id: replacementCatId });
      setDeletingCatId(null);
      setReplacementCatId("");
      await reload();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setCategoryError(typeof detail === "object" ? detail.message : (detail || "Kategorierne kunne ikke sammenlægges"));
    } finally {
      setCategoryBusy(false);
    }
  };
  const saveCommission = async () => {
    if (!Number.isFinite(commission) || commission < 0 || commission > 0.5) return;
    setCommissionBusy(true); setCommissionMessage("");
    try { await api.put("/admin/settings/commission", { value: commission }); setCommissionMessage("Provisionen er gemt."); }
    catch { setCommissionMessage("Provisionen kunne ikke gemmes. Prøv igen."); }
    finally { setCommissionBusy(false); }
  };
  const decideQualification = async (claim, status) => {
    const note = window.prompt(status === "verified" ? "Valgfri feedback til eksperten:" : "Forklar kort hvad der skal rettes:", "");
    if (note === null) return;
    const adminNote = window.prompt("Privat adminnote (vises ikke til eksperten):", claim.admin_note || "");
    if (adminNote === null) return;
    await api.post(`/admin/professional-badges/${claim.id}/decision`, { status, note: note || null, admin_note: adminNote || null });
    await loadQualifications();
  };
  const updateSupportCase = async (supportCase, status) => {
    const adminNote = window.prompt("Privat adminnote:", supportCase.admin_note || "");
    if (adminNote === null) return;
    try {
      await api.post(`/admin/support-cases/${supportCase.id}/status`, { status, admin_note: adminNote || null });
      await loadSupportCases();
    } catch (error) {
      alert(error.response?.data?.detail || "Sagen kunne ikke opdateres");
    }
  };

  const viewQualificationEvidence = async (claim) => {
    try {
      const response = await api.get(`/qualification-evidence/${claim.evidence_file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      alert(error.response?.data?.detail || "Dokumentationen kunne ikke åbnes");
    }
  };

  return (
    <AdminWorkspace tab={tab} onNavigate={navigateAdmin} pendingExperts={expertCounts.pending}>
      {loadError && <div role="alert" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{loadError} <button type="button" className="ml-2 underline" onClick={() => reload(expertView)}>Prøv igen</button></div>}
      {tab === "referrals" && <AdminReferrals />}
      {tab === "metrics" && <AdminOverview metrics={metrics} pendingExperts={loadError ? null : expertCounts.pending} waitingRequests={loadError ? null : problems.filter((problem) => requestMatchesView(problem.status, "waiting")).length} onNavigate={navigateAdmin}/>}

      {tab === "analytics" && (
        <AnalyticsStudio
          data={analytics}
          range={analyticsRange}
          setRange={setAnalyticsRange}
          loading={analyticsLoading}
          error={analyticsError}
        />
      )}

      {tab === "support" && (
        <section className="mt-8" data-testid="admin-support-cases">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div><h2 className="font-display text-xl font-semibold text-brand-ink">Supportsager</h2><p className="mt-1 text-sm text-brand-muted">Betaling, uenighed og anmeldelser knyttet til konkrete aftaler.</p></div>
            <div className="flex gap-1">{[["open", "Åbne"], ["reviewing", "Behandles"], ["resolved", "Løste"], ["all", "Alle"]].map(([value, label]) => <button key={value} aria-pressed={supportView === value} onClick={() => setSupportView(value)} className={`chip ${supportView === value ? "!bg-brand-ink !text-white" : ""}`}>{label}</button>)}</div>
          </div>
          {supportError && <p role="alert" className="admin-notice">{supportError} <button className="chip" onClick={() => loadSupportCases().catch(() => setSupportError("Supportsagerne kunne ikke hentes. Prøv igen."))}>Prøv igen</button></p>}
          {!supportLoading && !supportError && <AdminSearch label="Søg i supportsager" value={supportSearch} onChange={setSupportSearch} count={visibleSupport.length} total={supportCases.length}/>}
          {supportLoading && <div className="mt-5 text-sm text-brand-muted">Indlæser supportsager…</div>}
          {!supportLoading && !supportError && <div className="mt-5 space-y-3">{visibleSupport.map((item) => <article key={item.id} className="card-outline p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold text-brand-ink">{item.problem_title || "Aftale"}</h3><span className="chip">{supportCategoryLabel(item.category)}</span><AdminStatus status={item.status}>{supportStatusLabel(item.status)}</AdminStatus></div>
                <details className="admin-details"><summary>Læs henvendelse og betalingsstatus</summary><p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-brand-body">{item.description}</p>
                <p className="mt-2 text-xs text-brand-muted">{item.reported_by_name} · {item.reported_by_email} · {new Date(item.created_at).toLocaleString("da-DK")}</p>
                <p className="mt-1 text-xs text-brand-muted">Betaling: {item.payment_status || "ikke registreret"} · Rolle: {item.reported_by_role === "customer" ? "kunde" : "ekspert"}</p>
                </details>{item.admin_note && <div className="mt-3 rounded-lg bg-[#FFF8E7] px-3 py-2 text-xs text-[#6B4F00]">Privat note: {item.admin_note}</div>}
              </div>
              <div className="flex flex-wrap gap-2">
                {item.status !== "reviewing" && item.status !== "resolved" && <button onClick={() => updateSupportCase(item, "reviewing")} className="chip">Start behandling</button>}
                {item.status !== "resolved" && <button onClick={() => updateSupportCase(item, "resolved")} className="chip !border-[#9CC7B5] !text-brand-fresh">Markér løst</button>}
                {item.status === "resolved" && <button onClick={() => updateSupportCase(item, "open")} className="chip">Genåbn</button>}
              </div>
            </div>
          </article>)}{visibleSupport.length === 0 && <div className="admin-empty">Ingen supportsager i denne visning. Nye henvendelser vises her.</div>}</div>}
        </section>
      )}

      {tab === "problems" && (
        <section className="mt-8" data-testid="admin-problems-list">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-semibold text-brand-ink">Alle forespørgsler</h2>
              <p className="mt-1 text-sm text-brand-muted">Se kundens forespørgsel og følg den fra oprettelse til afslutning.</p>
            </div>
            <label className="w-full sm:w-72">
              <span className="sr-only">Søg i forespørgsler</span>
              <input value={problemSearch} onChange={(event) => setProblemSearch(event.target.value)} className="w-full card-outline px-3 py-2 text-sm" placeholder="Søg kunde, ekspert eller titel" data-testid="admin-request-search"/>
            </label>
          </div>

          <div className="mt-4 flex gap-1 overflow-x-auto pb-1" role="tablist" aria-label="Filtrér forespørgsler">
            {REQUEST_FILTERS.map(([value, label]) => {
              const count = problems.filter((problem) => requestMatchesView(problem.status, value)).length;
              return <button key={value} onClick={() => setProblemView(value)} className={`chip shrink-0 ${problemView === value ? "!bg-brand-ink !text-white" : ""}`} role="tab" aria-selected={problemView === value}>{label} <span className="ml-1 opacity-70">{count}</span></button>;
            })}
          </div>

          <p className="admin-result-count" role="status">{visibleProblems.length} forespørgsler i denne visning</p>
          <div className="mt-5 space-y-3">
            {visibleProblems.map((p) => {
              const expanded = expandedProblemId === p.id;
              const acceptedTerms = p.accepted_terms || {};
              const agreedPrice = acceptedTerms.fixed_price || acceptedTerms.estimated_price_max || acceptedTerms.estimated_price_min;
              return <article key={p.id} className="card-outline overflow-hidden" data-testid={`admin-request-${p.id}`}>
                <div className="p-4 sm:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-display font-semibold text-brand-ink">{p.title || "Forespørgsel uden titel"}</h3>
                        <AdminStatus status={p.status}>{requestStatusLabel(p.status)}</AdminStatus>
                      </div>
                      <p className={`mt-2 whitespace-pre-wrap text-sm leading-6 text-brand-body ${expanded ? "" : "line-clamp-2"}`}>{p.description || "Ingen beskrivelse"}</p>
                    </div>
                    <button type="button" onClick={() => setExpandedProblemId(expanded ? null : p.id)} className="chip shrink-0" aria-expanded={expanded}>{expanded ? "Skjul detaljer" : "Se detaljer"}</button>
                  </div>

                  <div className="mt-4 grid gap-3 border-t border-brand-line pt-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    <RequestFact label="Kunde" value={p.customer_name || "Ukendt kunde"} subvalue={p.customer_email}/>
                    <RequestFact label="Kategori" value={p.category_name || "Ikke angivet"}/>
                    <RequestFact label="Valgt ekspert" value={p.accepted_expert_name || "Ikke valgt"} subvalue={p.accepted_expert_email}/>
                    <RequestFact label="Prisforslag" value={`${p.proposal_count || 0} modtaget`} subvalue={agreedPrice ? `Aftalt ${formatDkk(agreedPrice)}` : null}/>
                  </div>

                  {expanded && <div className="mt-4 grid gap-3 rounded-xl bg-brand-wash p-4 text-sm sm:grid-cols-2 lg:grid-cols-3" data-testid={`admin-request-details-${p.id}`}>
                    <RequestFact label="Oprettet" value={readableDate(p.created_at)}/>
                    <RequestFact label="Aftalt tidspunkt" value={readableDate(p.scheduled_at)}/>
                    <RequestFact label="Betaling" value={({ paid: "Betalt", awaiting_payment: "Afventer betaling", refunded: "Refunderet", partially_refunded: "Delvist refunderet" }[p.payment_status] || "Ikke betalt")}/>
                    <RequestFact label="Tidspunkt" value={p.consultation_schedule_state === "confirmed" ? "Aftalt" : "Ikke aftalt"}/>
                    <RequestFact label="Samtale" value={p.status === "completed" ? "Afsluttet" : p.scheduled_at ? "Kommende" : "Ikke planlagt"}/>
                    <RequestFact label="Pengestatus" value={({ held: "Hos SpørgMig", released: "Frigivet", refunded: "Refunderet", partially_refunded: "Delvist refunderet" }[p.funds_state] || "Ikke registreret")}/>
                    {acceptedTerms.scope && <div className="sm:col-span-2 lg:col-span-3"><div className="text-[10px] uppercase tracking-widest text-brand-muted">Aftalt omfang</div><div className="mt-1 whitespace-pre-wrap text-brand-ink">{acceptedTerms.scope}</div></div>}
                  </div>}
                </div>
              </article>;
            })}
            {visibleProblems.length === 0 && <div className="rounded-xl border border-dashed border-brand-line p-8 text-center text-sm text-brand-muted">Ingen forespørgsler matcher din søgning eller dit filter.</div>}
          </div>
        </section>
      )}

      {tab === "experts" && (
        <div className="mt-8">
          <div className="mb-5">
            <h2 className="font-display text-xl font-semibold text-brand-ink">Ekspertadministration</h2>
            <p className="mt-1 text-sm text-brand-muted">Godkend rigtige ekspertansøgninger og administrer dem separat fra eksempelprofiler.</p>
            <div className="mt-4 flex items-center gap-1 overflow-x-auto border-b border-brand-line" role="tablist" aria-label="Ekspertstatus">
              {[
                ["all", "Alle"],
                ["pending", "Afventer godkendelse"],
                ["approved", "Godkendte"],
                ["draft", "Kladder"],
                ["rejected", "Afviste"],
                ["demo", "Demo/test"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => {
                    setExpertView(key);
                    loadExperts(key).catch(() => {});
                  }}
                  className={`relative inline-flex min-h-10 shrink-0 items-center gap-2 px-4 text-sm font-medium ${
                    expertView === key ? "text-brand-green" : "text-brand-body hover:text-brand-ink"
                  }`}
                  data-testid={`admin-experts-view-${key}`}
                  role="tab"
                  aria-selected={expertView === key}
                >
                  {label}
                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-soft px-1.5 text-[10px] text-brand-body">{expertCounts[key]}</span>
                  {expertView === key && <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand-green"/>}
                </button>
              ))}
            </div>
          </div>
          {expertView === "demo" && (
            <div className="mb-4 rounded-xl border border-[#E8D39A] bg-[#FFF9E8] p-4 text-sm text-[#6B4C13]" data-testid="admin-demo-experts-notice">
              Demo- og testprofiler er kun eksempeldata. De vises ikke på den offentlige markedsplads, kan ikke bookes og tæller ikke med i produktions-KPI’er.
            </div>
          )}
          <div className="space-y-3">
          <AdminSearch label="Søg efter ekspert" value={expertSearch} onChange={setExpertSearch} count={visibleExperts.length} total={experts.length}/>
          {visibleExperts.map((e) => (
            <div key={e.id} className="card-outline p-4" data-testid={`admin-expert-${e.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="admin-person-name">{e.name}</div><div className="admin-person-email">{e.email}</div>
                  <div className="text-xs text-brand-body">{e.headline}{e.specialties?.length ? ` · ${e.specialties.join(", ")}` : ""}</div>
                  {e.background_type && <div className="mt-1 text-xs text-brand-body">Faglig baggrund: {expertBackgroundLabel(e.background_type)} <span className="text-brand-muted">(selvangivet)</span></div>}
                  {e.custom_category && <div className="mt-1 text-xs font-medium text-brand-green">Andet fagområde: {e.custom_category}</div>}
                  <div className="mt-3"><AdminStatus status={e.admin_status}>{e.is_demo_account ? "Eksempelprofil" : expertStatusLabel(e.admin_status)}</AdminStatus></div>
                  {(e.professional_badges || []).filter((badge) => badge.status === "verified").length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(e.professional_badges || []).filter((badge) => badge.status === "verified").map((badge) => <span key={badge.id || badge.label} className="chip">{badge.label}</span>)}
                    </div>
                  )}
                </div>
                <div className="admin-row-actions">
                  <button onClick={() => setReviewExpertId(e.id)} className="chip" data-testid={`review-full-profile-${e.id}`}>Se fuld profil</button>
                  {!e.is_demo_account && e.admin_status !== "draft" && e.admin_status !== "approved" && <button onClick={() => decideExpert(e.id, "approved")} className="chip !bg-brand-fresh !text-white !border-brand-fresh" data-testid={`approve-${e.id}`}>Godkend</button>}
                  {!e.is_demo_account && e.admin_status !== "draft" && e.admin_status !== "rejected" && <button onClick={() => decideExpert(e.id, "rejected")} className="chip !bg-[#991B1B] !text-white !border-[#991B1B]" data-testid={`reject-${e.id}`}>Afvis</button>}
                  {e.is_demo_account && <button onClick={() => removeAccount({ id: e.user_id, name: e.name, email: e.email })} disabled={removingUserId === e.user_id} className="chip !text-[#991B1B] !border-[#D9A6A6] disabled:opacity-50" data-testid={`delete-demo-${e.id}`}>{removingUserId === e.user_id ? "…" : "Slet demo"}</button>}
                </div>
              </div>

            </div>
          ))}
          {visibleExperts.length === 0 && (
            <div className="admin-empty">
              {expertSearch ? "Ingen eksperter matcher din søgning." : expertView === "pending" ? "Ingen eksperter afventer godkendelse." : expertView === "draft" ? "Ingen ufærdige ekspertprofiler." : expertView === "rejected" ? "Ingen afviste eksperter." : expertView === "demo" ? "Ingen demo- eller testeksperter." : "Ingen eksperter fundet."}
            </div>
          )}
          </div>
        </div>
      )}

      {tab === "qualifications" && (
        <section className="mt-8" data-testid="admin-qualifications">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-xl font-semibold text-brand-ink">Dokumenter til gennemgang</h2>
              <p className="mt-1 text-sm text-brand-muted">Åbn dokumentationen og vurder den enkelte kvalifikation. Ekspertprofilen godkendes separat.</p>
            </div>
            <div className="flex gap-1">
              {[["pending", "Afventer"], ["verified", "Godkendte"], ["rejected", "Afviste"], ["all", "Alle"]].map(([value, label]) => <button key={value} aria-pressed={qualificationView === value} onClick={() => setQualificationView(value)} className={`chip ${qualificationView === value ? "!bg-brand-ink !text-white" : ""}`}>{label}</button>)}
            </div>
          </div>
          {qualificationError && <p role="alert" className="admin-notice">{qualificationError} <button className="chip" onClick={() => loadQualifications().catch(() => setQualificationError("Dokumenterne kunne ikke hentes. Prøv igen."))}>Prøv igen</button></p>}
          {!qualificationLoading && !qualificationError && <AdminSearch label="Søg i dokumenter" value={qualificationSearch} onChange={setQualificationSearch} count={visibleQualifications.length} total={qualificationClaims.length}/>}
          {qualificationLoading && <div className="mt-5 text-sm text-brand-muted">Indlæser kvalifikationer…</div>}
          {!qualificationLoading && !qualificationError && <div className="mt-5 space-y-3">{visibleQualifications.map((claim) => <div key={claim.id} className="card-outline p-4"><div className="flex items-start justify-between gap-4 flex-wrap"><div className="min-w-0"><div className="font-medium text-brand-ink">{claim.title || claim.label}</div><div className="mt-1 text-xs text-brand-body">{claim.expert_name} · {claim.expert_email}</div><details className="admin-details"><summary>Se dokumentoplysninger</summary><div className="mt-3 text-xs text-brand-muted">{qualificationTypeLabel(claim.type || claim.badge_type)}{claim.issuer ? ` · ${claim.issuer}` : ""}{claim.issued_at ? ` · udstedt ${claim.issued_at.slice(0, 10)}` : ""}{claim.expires_at ? ` · udløber ${claim.expires_at.slice(0, 10)}` : ""}</div>{claim.company_name && <div className="mt-1 text-xs text-brand-body">Virksomhed: {claim.company_name}{claim.cvr_number ? ` · CVR ${claim.cvr_number}` : ""}{claim.company_role ? ` · Rolle: ${claim.company_role}` : ""}</div>}{claim.authorization_number && <div className="mt-1 text-xs text-brand-body">Aut.nr.: {claim.authorization_number}</div>}{claim.description && <p className="mt-2 text-sm text-brand-body whitespace-pre-wrap">{claim.description}</p>}</details>{claim.admin_note && <div className="mt-2 rounded-lg bg-[#FFF8E7] px-2 py-1.5 text-xs text-[#6B4F00]">Privat adminnote: {claim.admin_note}</div>}<div className={`mt-2 text-xs ${claim.expired ? "text-[#991B1B]" : claim.status === "verified" ? "text-brand-fresh" : claim.status === "rejected" ? "text-[#991B1B]" : "text-brand-muted"}`}>{claim.expired ? "Udløbet" : claim.status === "verified" ? "Verificeret" : claim.status === "rejected" ? "Afvist" : "Afventer verificering"}</div><div className="mt-1 text-[11px] text-brand-muted">Indsendt {claim.submitted_at ? new Date(claim.submitted_at).toLocaleString("da-DK") : "—"}{claim.reviewed_at ? ` · behandlet ${new Date(claim.reviewed_at).toLocaleString("da-DK")}` : ""}</div></div><div className="admin-row-actions">{claim.evidence_file_id && <button onClick={() => viewQualificationEvidence(claim)} className="chip">Se dokument</button>}{claim.status === "pending" && <><button onClick={() => decideQualification(claim, "verified")} className="chip !bg-brand-fresh !text-white !border-brand-fresh">Godkend</button><button onClick={() => decideQualification(claim, "rejected")} className="chip !bg-[#991B1B] !text-white !border-[#991B1B]">Afvis</button></>}</div></div></div>)}{visibleQualifications.length === 0 && <div className="admin-empty">Ingen dokumenter at gennemgå i denne visning.</div>}</div>}
        </section>
      )}

      {tab === "users" && (
        <div className="mt-8 space-y-2">
          <div className="admin-user-toolbar">
            <label>Søg efter bruger<input type="search" value={userSearch} onChange={(event) => setUserSearch(event.target.value)} placeholder="Navn eller e-mail"/></label>
            <label>Kontotype<select value={userRole} onChange={(event) => setUserRole(event.target.value)}><option value="all">Alle kontotyper</option><option value="customer">Kunder</option><option value="expert">Eksperter</option><option value="admin">Administratorer</option></select></label>
            <p aria-live="polite">{visibleUsers.length} af {users.length} brugere</p>
          </div>
          {visibleUsers.length === 0 && <div className="admin-empty">Ingen brugere matcher din søgning.</div>}
          {visibleUsers.map((u) => (
            <div key={u.id} className="card-outline p-3 flex items-center justify-between gap-3 text-sm flex-wrap" data-testid={`admin-user-${u.id}`}>
              <div>
                <div className="min-w-0"><span className="block font-semibold text-brand-ink">{u.name}</span><span className="block break-all text-brand-muted">{u.email}</span></div>
                <div className="mt-1 text-xs text-brand-muted">{({ customer: "Kunde", expert: "Ekspert", admin: "Administrator" }[u.role] || u.role)}{u.is_demo || u.is_test ? " · Demo/test" : ""}{u.deactivated ? " · Deaktiveret" : ""}</div>
              </div>
              {u.role !== "admin" && !u.deactivated && (
                <button onClick={() => removeAccount(u)} disabled={removingUserId === u.id} className="chip !text-[#991B1B] !border-[#D9A6A6] disabled:opacity-50" data-testid={`remove-user-${u.id}`}>
                  {removingUserId === u.id ? "…" : (u.is_demo || u.is_test ? "Slet testkonto" : "Fjern konto")}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "categories" && (
        <div className="mt-8">
          <details className="admin-form-panel"><summary>Opret kategori</summary><p>Tilføj et fagområde som kunder og eksperter kan vælge.</p><div className="admin-create-category">
            <input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} placeholder="Kategorinavn" aria-label="Kategorinavn" className="card-outline px-3 py-2 flex-1"/>
            <input value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} placeholder="URL-navn, fx it-teknologi" aria-label="Kategoriens URL-navn" className="card-outline px-3 py-2 flex-1"/>
            <button onClick={addCat} className="btn-primary">Tilføj kategori</button>
          </div></details>
          <AdminSearch label="Søg i kategorier" value={categorySearch} onChange={setCategorySearch} count={visibleCategories.length} total={cats.length}/>
          {visibleCategories.length === 0 && <div className="admin-empty">Ingen kategorier matcher din søgning.</div>}
          <div className="mt-4 space-y-2">
            {visibleCategories.map((c) => (
              <div key={c.id} className="card-outline p-3">
                {editingCatId === c.id ? (
                  <div className="flex flex-wrap gap-2">
                    <input value={editingCat.name} onChange={(event) => setEditingCat((current) => ({ ...current, name: event.target.value }))} className="card-outline min-w-0 flex-1 px-3 py-2" aria-label="Kategorinavn"/>
                    <input value={editingCat.slug} onChange={(event) => setEditingCat((current) => ({ ...current, slug: event.target.value }))} className="card-outline min-w-0 flex-1 px-3 py-2" aria-label="Kategori-URL"/>
                    <button onClick={() => saveCat(c.id)} className="btn-primary">Gem</button>
                    <button onClick={() => setEditingCatId(null)} className="chip">Annuller</button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div><div className="font-medium text-brand-ink">{c.name}</div><div className="mt-0.5 text-xs text-brand-muted">/kategorier/{c.slug}</div></div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingCatId(c.id); setEditingCat({ name: c.name, slug: c.slug }); }} className="chip">Rediger</button>
                      <button onClick={() => { setDeletingCatId(c.id); setReplacementCatId(""); setCategoryError(""); }} className="text-xs text-[#991B1B]">Slet</button>
                    </div>
                  </div>
                )}
                {deletingCatId === c.id && (
                  <div className="mt-3 rounded-xl border border-[#E8CFCF] bg-[#FFF9F7] p-4" data-testid={`delete-category-${c.id}`}>
                    <div className="font-medium text-brand-ink">Slet {c.name}?</div>
                    <p className="mt-1 text-sm text-brand-body">
                      Er kategorien i brug, skal eksperter, forespørgsler, blogindlæg og statistik først flyttes til en anden kategori.
                    </p>
                    <label className="mt-3 block text-xs font-medium text-brand-body" htmlFor={`replacement-${c.id}`}>Flyt eksisterende data til</label>
                    <select
                      id={`replacement-${c.id}`}
                      value={replacementCatId}
                      onChange={(event) => { setReplacementCatId(event.target.value); setCategoryError(""); }}
                      className="card-outline mt-1 w-full px-3 py-2 text-sm"
                    >
                      <option value="">Vælg en kategori</option>
                      {cats.filter((item) => item.id !== c.id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                    {categoryError && <p className="mt-2 text-sm text-[#991B1B]" role="alert">{categoryError}</p>}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={() => delCat(c.id)} disabled={categoryBusy} className="chip !border-[#D9A6A6] !text-[#991B1B] disabled:opacity-50">Slet hvis ubrugt</button>
                      <button onClick={() => mergeCat(c.id)} disabled={!replacementCatId || categoryBusy} className="btn-primary disabled:opacity-50">Sammenlæg og slet</button>
                      <button onClick={() => { setDeletingCatId(null); setReplacementCatId(""); setCategoryError(""); }} disabled={categoryBusy} className="chip">Annuller</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="admin-settings-panel">
          <label htmlFor="admin-commission" className="block font-semibold text-brand-ink">Platformens provision</label>
          <p className="mt-2 text-sm text-brand-muted">Den valgte sats er {Number.isFinite(commission) ? `${Math.round(commission * 10000) / 100}%` : "ikke angivet"}.</p>
          <input id="admin-commission" aria-describedby="admin-commission-help" type="number" step="0.01" min="0" max="0.5" value={Number.isFinite(commission) ? commission : ""} onChange={(e) => setCommission(parseFloat(e.target.value))} className="mt-2 card-outline px-3 py-2 w-full"/>
          <div id="admin-commission-help" className="text-xs text-brand-muted mt-2">Angiv som decimaltal: 0,20 svarer til 20%. Tilladt interval: 0 til 0,50.</div>
          <div className="admin-form-footer"><p>Gem for at anvende den valgte sats.</p><button disabled={commissionBusy || !Number.isFinite(commission) || commission < 0 || commission > 0.5} onClick={saveCommission} className="btn-primary" data-testid="save-commission">{commissionBusy ? "Gemmer…" : "Gem provision"}</button></div>{commissionMessage && <p className="admin-notice" role="status">{commissionMessage}</p>}
        </div>
      )}
      {tab === "seo" && <AdminSeoLandings/>}
      <AdminExpertReviewDialog
        expertId={reviewExpertId}
        categories={cats}
        onClose={() => setReviewExpertId(null)}
        onViewEvidence={viewQualificationEvidence}
        onDecision={async (expertId, decision, adminNote) => {
          await decideExpert(expertId, decision, adminNote);
          setReviewExpertId(null);
        }}
      />
    </AdminWorkspace>
  );
}

function RequestFact({ label, value, subvalue }) {
  return <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-widest text-brand-muted">{label}</div>
    <div className="mt-1 break-words font-medium text-brand-ink">{value || "—"}</div>
    {subvalue && <div className="mt-0.5 break-words text-xs text-brand-muted">{subvalue}</div>}
  </div>;
}

function expertStatusLabel(status) {
  return {
    approved: "Godkendt",
    pending: "Afventer godkendelse",
    rejected: "Afvist",
    draft: "Kladde",
  }[status] || "Ukendt";
}

function supportCategoryLabel(category) {
  return { payment: "Betaling", dispute: "Uenighed", safety: "Utryg adfærd", other: "Andet" }[category] || category;
}

function supportStatusLabel(status) {
  return { open: "Åben", reviewing: "Behandles", resolved: "Løst" }[status] || status;
}

function qualificationTypeLabel(type) {
  return {
    education: "Uddannelse",
    certificate: "Certifikat",
    authorization: "Autorisation",
    affiliation: "Virksomhedstilknytning",
    company: "Virksomhedstilknytning",
    other: "Anden kvalifikation",
  }[type] || "Kvalifikation";
}
